import { binarySearchByIndex0 } from 'webui/utils/binary-search';
import { add_gaps, normalize_time, time_mul_1k } from './common';
export class Series {
    _title;
    _show_in_legend;
    _color;
    constructor({ title, show_in_legend = true, color }) {
        this._title = title;
        this._show_in_legend = show_in_legend;
        this._color = color;
    }
    yaxis_key = () => {
        return null;
    };
    title = () => {
        return this._title;
    };
    show_in_legend = () => {
        return this._show_in_legend;
    };
    color = () => {
        return this._color;
    };
    get_markings_data = () => {
        return null;
    };
    html_formatter = (x) => {
        return '';
    };
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    stats_html = (props) => {
        return null;
    };
}
export class LineSeries extends Series {
    _data;
    _yaxis_key;
    _shadow_size;
    _dont_downsample;
    _curvedLines;
    constructor({ data, yaxis_key, shadow_size = 1, dont_downsample = false, curvedLines = undefined, }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._data = data;
        this._yaxis_key = yaxis_key;
        this._shadow_size = shadow_size;
        this._dont_downsample = dont_downsample;
        this._curvedLines = curvedLines;
    }
    yaxis_key = () => {
        return this._yaxis_key;
    };
    data = () => {
        return this._data;
    };
    create_flot_series = () => {
        return {
            data: this._data,
            dont_downsample: this._dont_downsample,
            shadowSize: this._shadow_size,
            lines: {
                lineWidth: 1.5,
            },
            color: this.color(),
            curvedLines: this._curvedLines,
        };
    };
}
export class MarkingsSeries extends Series {
    _data;
    constructor({ data }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._data = data;
    }
    get_markings_data = () => {
        return { data: this._data, color: this._color };
    };
}
export class BottomMarkingsSeries extends Series {
    _data;
    constructor({ data }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._data = data;
    }
    get_markings_data = () => {
        return { data: this._data, color: this._color };
    };
}
export class TooltipSeries extends Series {
    _data;
    _html_formatter;
    constructor({ data, html_formatter }) {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        super(...arguments);
        this._data = data;
        this._html_formatter = html_formatter;
    }
    show_in_legend = () => {
        return false;
    };
    get_tooltip_html_last = () => {
        if (this._data != null) {
            const item = this._data[this._data.length - 1]?.[1];
            return this._html_formatter(item);
        }
        else
            return null;
    };
    get_tooltip_html = (time) => {
        if (this._data == null) {
            return null;
        }
        const { l, r } = binarySearchByIndex0(this._data, time);
        if (r === this._data.length) {
            return null;
        }
        const item = this._data[l];
        if (item?.[1] == null) {
            return null;
        }
        return this._html_formatter(item[1]);
    };
}
export class GraphSource {
    _color;
    class_name() {
        return 'GraphSource';
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor(device) { }
    set_color(color) {
        this._color = color;
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    title() {
        return '';
    }
    show_in_legend(data) {
        return true;
    }
    legend_type() {
        return 'series';
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    color() {
        return '';
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    data_source_key() {
        return '';
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    yaxis_key() {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    extract_data(raw_data) {
        return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    normalize_data(data) {
        return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    serialize_params() {
        return { color: this._color };
    }
    serialize() {
        const result = { class: this.class_name(), params: {} };
        const params = this.serialize_params();
        if (params != null) {
            result.params = params;
        }
        return result;
    }
    is_same(other) {
        return this.constructor === other.constructor;
    }
    get_series(xbounds, raw_data) {
        let norm = this.extract_data(raw_data);
        if (norm != null) {
            norm = this.normalize_data(norm);
        }
        if (norm != null) {
            return this.series_from_norm_data(xbounds, norm);
        }
        return [];
    }
    dont_downsample() {
        return false;
    }
    curvedLines() {
        return undefined;
    }
    series_from_norm_data(xbounds, normalized_data) {
        return [
            new LineSeries({
                title: this.title(),
                show_in_legend: this.show_in_legend(normalized_data),
                color: this.color(),
                yaxis_key: this.yaxis_key(),
                data: normalized_data,
                dont_downsample: this.dont_downsample(),
                curvedLines: this.curvedLines(),
            }),
        ];
    }
}
export class TemperatureGraphData extends GraphSource {
    yaxis_key() {
        return 'temperature';
    }
    normalize_data(data) {
        return time_mul_1k(add_gaps(normalize_time(data, 0)));
    }
}
export class BooleanMarkingsGraphSource extends GraphSource {
    predicate(value) {
        return false;
    }
    legend_type() {
        return 'markings';
    }
    markings_type() {
        return 'normal';
    }
    series_from_norm_data(xbounds, data) {
        const markings = this.create_markings(xbounds, data);
        if (markings == null) {
            return [];
        }
        const params = {
            title: this.title(),
            color: this.color(),
            show_in_legend: true,
            data: markings,
        };
        const klass = this.markings_type() === 'bottom' ? BottomMarkingsSeries : MarkingsSeries;
        return [new klass(params)];
    }
    _add_to_boolean_markings(markings, time, value, predicate, color) {
        const last = markings[markings.length - 1];
        //@ts-ignore
        if (last?.__open) {
            //@ts-ignore
            last.xaxis.to = time * 1e3;
            //@ts-ignore
            last.__open = predicate(value);
            return true;
        }
        if (predicate(value)) {
            markings.push({
                xaxis: {
                    from: time * 1e3,
                    to: time * 1e3,
                },
                //@ts-ignore
                color,
                __open: true,
            });
            return true;
        }
        return false;
    }
    create_markings(xbounds, data) {
        const markings = [];
        for (const [time, value] of data)
            this._add_to_boolean_markings(
            //@ts-ignore
            markings, time, value, this.predicate.bind(this), this.color());
        return markings;
    }
}
export class MultipleColorsMarkings extends GraphSource {
    value2color(value) {
        return null;
    }
    legend_type() {
        return 'markings';
    }
    show_in_legend(markings) {
        return (markings != null ? markings.length : 0) > 0;
    }
    series_from_norm_data(xbounds, data) {
        const markings = this.create_markings(xbounds, data);
        if (markings == null)
            return [];
        return [
            new MarkingsSeries({
                title: this.title(),
                color: this.color(),
                //@ts-ignore
                show_in_legend: this.show_in_legend(markings),
                //@ts-ignore
                data: markings,
            }),
        ];
    }
    _add_to_markings(markings, time, value, matches, color) {
        const last = markings[markings.length - 1];
        //@ts-ignore
        if (last?.__open) {
            //@ts-ignore
            last.xaxis.to = time * 1e3;
            //@ts-ignore
            last.__open = matches;
            return true;
        }
        if (matches) {
            markings.push({
                xaxis: {
                    from: time * 1e3,
                    to: time * 1e3,
                },
                //@ts-ignore
                color,
                __open: true,
            });
            return true;
        }
        return false;
    }
    create_markings(xbounds, data) {
        const color2markings = {};
        for (const [time, value] of data) {
            //@ts-ignore
            const color = this.value2color(value);
            if (color != null && color2markings[color] == null)
                color2markings[color] = [];
            for (const [another_color, markings] of Object.entries(color2markings || {}))
                this._add_to_markings(markings, time, value, color === another_color, color);
        }
        return Object.values(color2markings ?? {}).flat();
    }
}
